import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../ui/button';
import { Separator } from '../../ui/separator';
import { RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ResetScopeSelector from './ResetScopeSelector';
import ResetUserSelector from './ResetUserSelector';
import ResetConfirmDialog from './ResetConfirmDialog';
import ResetHistoryList from './ResetHistoryList';

/**
 * ProgressResetTool - reset learner progress for one course of any course builder.
 * source: 'tech-learning' | 'finance-training' | <custom module slug>
 */
const ProgressResetTool = ({ api, source, courseId }) => {
  const base = `/progress-reset/${source}/courses/${courseId}`;
  const [modules, setModules] = useState([]);
  const [department, setDepartment] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [scope, setScope] = useState('course');
  const [selectedModules, setSelectedModules] = useState(new Set());
  const [selectedSubs, setSelectedSubs] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    api.get(`${base}/structure`)
      .then(res => setModules(res.data.modules || []))
      .catch(() => setModules([]));
  }, [api, base]);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get(`${base}/users?department=${encodeURIComponent(department)}`);
      const list = res.data.users || [];
      setUsers(list);
      setDepartments(res.data.departments || []);
      setSelectedUsers(new Set(list.map(u => u.id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load assigned users');
    } finally {
      setLoadingUsers(false);
    }
  }, [api, base, department]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const toggleInSet = (setter) => (id) => setter(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleModule = (module) => {
    toggleInSet(setSelectedModules)(module.id);
    setSelectedSubs(prev => {
      const next = new Set(prev);
      module.sub_modules.forEach(sm => next.delete(sm.id));
      return next;
    });
  };

  const toggleAllUsers = () => setSelectedUsers(prev =>
    prev.size === users.length ? new Set() : new Set(users.map(u => u.id))
  );

  const payload = () => ({
    user_ids: [...selectedUsers],
    scope,
    module_ids: scope === 'items' ? [...selectedModules] : [],
    sub_module_ids: scope === 'items' ? [...selectedSubs] : [],
    department
  });

  const canSubmit = selectedUsers.size > 0 && (scope === 'course' || selectedModules.size + selectedSubs.size > 0);

  const openPreview = async () => {
    setBusy(true);
    try {
      const res = await api.post(`${base}/preview`, payload());
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to prepare reset');
    } finally {
      setBusy(false);
    }
  };

  const confirmReset = async () => {
    setBusy(true);
    try {
      const res = await api.post(`${base}/reset`, payload());
      toast.success(res.data.message);
      setPreview(null);
      setSelectedModules(new Set());
      setSelectedSubs(new Set());
      setHistoryKey(k => k + 1);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset progress');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="progress-reset-tool">
      <ResetUserSelector
        departments={departments}
        department={department}
        onDepartmentChange={setDepartment}
        users={users}
        loading={loadingUsers}
        selectedUsers={selectedUsers}
        onToggleUser={toggleInSet(setSelectedUsers)}
        onToggleAll={toggleAllUsers}
      />
      <ResetScopeSelector
        modules={modules}
        scope={scope}
        onScopeChange={setScope}
        selectedModules={selectedModules}
        selectedSubs={selectedSubs}
        onToggleModule={toggleModule}
        onToggleSub={toggleInSet(setSelectedSubs)}
      />
      <Button
        onClick={openPreview}
        disabled={!canSubmit || busy}
        className="bg-amber-600 hover:bg-amber-700"
        data-testid="reset-progress-submit-btn"
      >
        {busy && !preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
        Reset Progress
      </Button>
      <Separator />
      <ResetHistoryList api={api} source={source} courseId={courseId} refreshKey={historyKey} />
      <ResetConfirmDialog preview={preview} busy={busy} onCancel={() => setPreview(null)} onConfirm={confirmReset} />
    </div>
  );
};

export default ProgressResetTool;
